import { Check, ChevronRight, Clock3, FileInput, Minus, X } from 'lucide-react';
import { Toolbar } from 'primereact/toolbar';
import { Tooltip } from 'primereact/tooltip';
import { Dispatch, FC, SetStateAction } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { IndividualStudentReportStatsQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportStatsQuery.graphql';
import { ExitSlipModalData } from './IndividualReportDailyActivity';
import { groupExitSlipResponsesByItem } from '../../businessLogicService/exitSlipItemScores';

interface Props {
  exitSlip: IndividualStudentReportStatsQuery['response']['classExitSlips'][0];
  setModalData: Dispatch<SetStateAction<ExitSlipModalData>>;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
}

const IndividualReportDailyExitSlip: FC<Props> = ({ exitSlip, setModalData, setIsVisible }) => {
  const userRedux = useAppSelector(selectUser);

  const session = exitSlip?.learnosityExitSlipSessions?.[0];
  const items = session?.items ? groupExitSlipResponsesByItem(session.items) : null;
  const scorePercent = items
    ? Math.round((items.filter(item => item.score === item.maxScore).length / (items.length || 1)) * 100)
    : null;
  const scoreBg =
    scorePercent === null
      ? 'tw-bg-neutral-100'
      : scorePercent > 66
      ? 'tw-bg-emerald-100'
      : scorePercent >= 50
      ? 'tw-bg-amber-100'
      : 'tw-bg-red-100';

  return (
    <Toolbar
      start={
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-x-2">
          <span className="nc_exit-slip-banner-icon-sm"></span>
          <span className="tw-text-base tw-font-atkinson tw-font-bold">Exit Ticket</span>
          <span className="tw-w-[2px] tw-h-[10px] tw-bg-neutral-300 tw-mx-2"></span>
          <span className="tw-text-base tw-font-atkinson ">
            <span className="tw-font-bold">Lesson {exitSlip.curriculumLesson?.number}: </span>
            {exitSlip.curriculumLesson?.name}
          </span>
          <span className="tw-w-[2px] tw-h-[10px] tw-bg-neutral-300"></span>
          <span className="tw-text-base tw-font-atkinson">Unit: {exitSlip.curriculumLesson?.target?.name}</span>
        </div>
      }
      center={
        session && items ? (
          <div className="tw-flex tw-flex-row tw-items-center tw-gap-x-2">
            <Tooltip target=".nc_submision-time" />
            <FileInput
              className="nc_submision-time"
              data-pr-tooltip="Submission time"
              data-pr-position="top"
              size={16}
            />
            <span className="tw-text-base tw-font-atkinson tw-font-bold">
              {new Date(session.sessionCompletedOn).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: userRedux?.activeTimeZone,
              })}
            </span>
            <span className="tw-w-[2px] tw-h-[10px] tw-bg-neutral-300 tw-mx-2"></span>
            <Tooltip target=".nc_time-spent" />
            <Clock3 className="nc_time-spent" data-pr-tooltip="Time spent" data-pr-position="top" size={16} />
            <span className="tw-text-base tw-font-atkinson tw-font-bold">
              {session.sessionLength > 60 ? '60+ m' : session.sessionLength + ' m'}
            </span>
          </div>
        ) : (
          <></>
        )
      }
      end={
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-x-1">
          {items && (
            <>
              <div className="tw-flex tw-flex-row">
                {items.map(el => {
                  const isCorrect = el.score === el.maxScore;
                  const isAttempted = el.attempted;
                  return (
                    <span
                      key={el.key}
                      className={`tw-rounded-[24px] tw-p-1 tw-mx-1 
                                    ${
                                      isCorrect
                                        ? 'tw-bg-emerald-100'
                                        : isAttempted
                                        ? 'tw-bg-red-100'
                                        : 'tw-bg-neutral-100'
                                    }
                                  `}
                    >
                      {isCorrect ? (
                        <Check size={16} className="tw-flex tw-text-emerald-900" />
                      ) : isAttempted ? (
                        <X size={16} className="tw-flex tw-text-red-900" />
                      ) : (
                        <Minus size={16} className="tw-flex tw-text-neutral-900" />
                      )}
                    </span>
                  );
                })}
              </div>
              <span className="tw-w-[2px] tw-h-[10px] tw-bg-neutral-300 tw-mx-2"></span>
            </>
          )}
          <span className={`${scoreBg} tw-rounded-[24px] tw-px-2 tw-py-1 tw-font-bold`}>
            {scorePercent === null ? 'Not Taken' : `${scorePercent}%`}
          </span>
          <ChevronRight
            size={16}
            className={`tw-text-blue-500 tw-cursor-pointer ${items ? '' : 'tw-hidden'} `}
            onClick={() => {
              setModalData({
                sessionId: session?.sessionId as string,
                activityId: session?.activityId as string,
                publicUserId: session?.publicUserId ?? null,
                title: exitSlip.curriculumLesson?.name ?? '',
              });
              setIsVisible(true);
            }}
          />
        </div>
      }
      className="tw-mb-4 tw-p-2 tw-bg-neutral-0 tw-border-2 tw-rounded-xl tw-flex tw-justify-between tw-border-solid tw-border-blue-50"
    />
  );
};

export default IndividualReportDailyExitSlip;
